/**
 * index.js — wiring for /hurricane-simulator.
 *
 * Structured exactly like its sibling /earthquake-simulator, and for the same
 * reason: every panel is a view of ONE storm object, and they all redraw from a
 * single apply() so they cannot fall out of step with each other.
 */

import { cyclone, BEAUFORT, SAFFIR_SIMPSON, beaufort, saffirSimpson,
         canFormAt, CORIOLIS_DEAD_ZONE_DEG, KMH_PER_MS } from '../cataclysm/cyclone-physics.js';
import { CYCLONES, BY_ID, toCycloneInput } from '../cataclysm/cyclone-catalogue.js';
import { buildIndex, citiesWithin } from '../cataclysm/exposure.js';
import { createMap, paintLegend } from './map.js';
import { draw as drawProfile } from './profile.js';

const CFG = window.HU_CFG || {};
const T = CFG.t || {};
const LOC = CFG.locale || 'en';

const $ = (id) => document.getElementById(id);
const fmt = (v, d = 0) => new Intl.NumberFormat(LOC, { maximumFractionDigits: d }).format(v);
const compact = (v) => {
    try { return new Intl.NumberFormat(LOC, { notation: 'compact', maximumFractionDigits: 1 }).format(v); }
    catch (e) { return fmt(v); }
};
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const state = {
    vmaxKmh: 250, forwardKmh: 20, headingDeg: 315,
    lat: 25.9, lon: -89.6,
    presetId: null, observedHpa: null,
    site: null, st: null,
};

let mapView = null, citiesReady = false;

/* ─────────────────────────────  the storm  ──────────────────────────── */

function build() {
    state.st = cyclone({
        vmaxMs: state.vmaxKmh / KMH_PER_MS,
        lat: state.lat, lon: state.lon,
        headingDeg: state.headingDeg,
        forwardMs: state.forwardKmh / KMH_PER_MS,
    });
    return state.st;
}

/**
 * Which place the card is about: the reader's, or the largest settlement that
 * is genuinely inside the storm. Same lesson as the earthquake tool — picking
 * the biggest city in the whole search radius produces a headline about a city
 * that felt a breeze.
 */
function resolveSite(st) {
    if (state.site) return state.site;
    if (!citiesReady) return null;

    /* Search WIDER than the gale radius. Storms reach their peak over open
       water — Katrina's peak position is in the middle of the Gulf, 450 km from
       New Orleans — so a search bounded by the storm's own footprint found no
       city at all and the card sat blank on a category 4 hurricane. Cast a wide
       net, then let the wind decide which place is worth the headline. */
    const reach = Math.max(600, (st.radiusOf(BEAUFORT[8].min) || 0) * 1.5);
    const near = citiesWithin(st.lat, st.lon, reach, 8000);
    if (!near.length) return null;

    let best = null, bestSpeed = 0;
    for (const c of near) {
        const w = st.windAt(c.lat, c.lon).speed;
        if (w > bestSpeed) bestSpeed = w;
    }
    // Nothing out there even feels a strong breeze: say so rather than
    // headlining a city where the trees are barely moving.
    if (bestSpeed < BEAUFORT[6].min) return null;

    for (const c of near) {
        const w = st.windAt(c.lat, c.lon).speed;
        if (w >= bestSpeed * 0.75 && (!best || c.pop > best.pop)) best = c;
    }
    return best ? { lat: best.lat, lon: best.lon, name: best.name, pop: best.pop, auto: true } : null;
}

/* ───────────────────────────────  render  ──────────────────────────── */

function renderAnswer(st, site) {
    const big = $('huBig');
    if (!site) {
        big.style.background = 'var(--bg-tertiary)';
        big.style.color = 'var(--text-secondary)';
        $('huBigNum').textContent = '—';
        $('huBigForce').textContent = '';
        $('huBigPlace').textContent = '';
        $('huDesc').textContent = T.no_cities || '';
        $('huFacts').innerHTML = '';
        renderWarn(st);
        return;
    }

    const w = st.windAt(site.lat, site.lon);
    const bf = w.beaufort, cat = w.category;
    const paint = cat || bf;
    big.style.background = paint.color;
    big.style.color = paint.ink;
    $('huBigNum').textContent = `${fmt(w.speed * KMH_PER_MS)}`;
    $('huBigForce').textContent = cat
        ? `${T.kmh} · ${T.cat_short}${cat.cat === 0 ? '—' : cat.cat}`
        : `${T.kmh} · ${T.force} ${bf.f}`;
    $('huBigPlace').textContent = site.name || '';

    /* Which scale describes the wind. Above hurricane force the Beaufort entry
       is force 12 for EVERYTHING, and its text is "the scale stops here" — true,
       and useless as the headline description of a category 4. Once a category
       exists, the category describes it; below that, Beaufort does, because
       that is the scale that actually resolves ordinary wind. */
    const forceCopy = (CFG.forces || {})[bf.f] || {};
    const catCopy = cat && cat.cat >= 1 ? ((CFG.cats || {})[cat.cat] || {}) : null;
    $('huDesc').textContent = (catCopy && catCopy.what) || forceCopy.what || '';

    const facts = [
        [T.from_eye, `${fmt(w.r, w.r < 20 ? 1 : 0)} ${T.km}`],
        [T.force, `${bf.f} — ${(forceCopy.name || '')}`],
        [T.direction, compass(w.dirDeg)],
        [T.speed, `${fmt(w.speed, 1)} ${T.ms}`],
        [T.rmax, `${fmt(st.rmax, 0)} ${T.km}`],
        [T.r_hurricane, st.radiusOf(SAFFIR_SIMPSON[1].minMs) ? `${fmt(st.radiusOf(SAFFIR_SIMPSON[1].minMs), 0)} ${T.km}` : '—'],
        [T.r_gale, st.radiusOf(BEAUFORT[8].min) ? `${fmt(st.radiusOf(BEAUFORT[8].min), 0)} ${T.km}` : '—'],
        [T.pressure, `${fmt(st.pressureHpa, 0)} ${T.hpa}`],
    ];
    $('huFacts').innerHTML = facts.map(([k, v]) =>
        `<div class="hu-fact"><span>${esc(k)}</span><b>${esc(v)}</b></div>`).join('');

    renderWarn(st);
}

function renderWarn(st) {
    const warns = [];
    if (!canFormAt(st.lat)) {
        warns.push((T.no_coriolis || '').replace(':deg', String(CORIOLIS_DEAD_ZONE_DEG)));
    }
    if (state.observedHpa != null) {
        warns.push((T.observed_pressure || '')
            .replace(':observed', fmt(state.observedHpa, 0))
            .replace(':model', fmt(st.pressureHpa, 0)));
    }
    const el = $('huWarn');
    el.innerHTML = warns.filter(Boolean).map(esc).join('<br>');
    el.hidden = warns.length === 0;
}

function compass(deg) {
    const pts = (T.compass || 'N,NE,E,SE,S,SW,W,NW').split(',');
    return pts[Math.round(deg / 45) % 8];
}

function renderCities(st) {
    const el = $('huCities');
    if (!citiesReady) { el.innerHTML = `<div class="hu-note">${esc(T.loading_cities)}</div>`; return; }
    const reach = Math.max(600, (st.radiusOf(BEAUFORT[6].min) || 0) * 1.2);
    const near = citiesWithin(st.lat, st.lon, reach, 8000)
        .map((c) => ({ ...c, w: st.windAt(c.lat, c.lon) }))
        .filter((c) => c.w.speed >= BEAUFORT[6].min)
        .sort((a, b) => b.w.speed - a.w.speed || b.pop - a.pop)
        .slice(0, 8);

    if (!near.length) { el.innerHTML = `<div class="hu-note">${esc(T.no_cities)}</div>`; return; }
    el.innerHTML = near.map((c) => {
        const paint = c.w.category || c.w.beaufort;
        const label = c.w.category
            ? (c.w.category.cat === 0 ? T.tropical_storm : `${T.cat_short}${c.w.category.cat}`)
            : `${c.w.beaufort.f}`;
        return `<div class="hu-city">
            <span class="b" style="background:${paint.color};color:${paint.ink}">${esc(label)}</span>
            <span class="n">${esc(c.name)}<small>${fmt(c.w.r, 0)} ${esc(T.km)}</small></span>
            <span class="p">${esc(fmt(c.w.speed * KMH_PER_MS))} ${esc(T.kmh)} · ${esc(compact(c.pop))}</span>
        </div>`;
    }).join('');
}

function apply({ refit = false } = {}) {
    const st = build();
    const site = resolveSite(st);
    mapView && mapView.render(st, site, T);
    if (refit) mapView && mapView.fit(st, site);
    renderAnswer(st, site);
    renderCities(st);
    drawProfile($('huProfile'), st, T);
}

/* ───────────────────────────────  inputs  ──────────────────────────── */

function setWind(kmh) {
    state.vmaxKmh = Math.round(kmh / 5) * 5;
    $('huWind').value = String(state.vmaxKmh);
    const cat = saffirSimpson(state.vmaxKmh / KMH_PER_MS);
    const tag = cat ? (cat.cat === 0 ? T.tropical_storm : `${T.cat_short}${cat.cat}`) : `${T.force} ${beaufort(state.vmaxKmh / KMH_PER_MS).f}`;
    $('huWindOut').textContent = `${fmt(state.vmaxKmh)} ${T.kmh} · ${tag}`;
}
function setForward(kmh) {
    state.forwardKmh = Math.round(kmh);
    $('huForward').value = String(state.forwardKmh);
    $('huForwardOut').textContent = `${state.forwardKmh} ${T.kmh}`;
}
function setHeading(deg) {
    state.headingDeg = ((Math.round(deg / 5) * 5) % 360 + 360) % 360;
    $('huHeading').value = String(state.headingDeg);
    $('huHeadingOut').textContent = `${state.headingDeg}° ${compass(state.headingDeg)}`;
}

function clearPreset() {
    if (!state.presetId) return;
    state.presetId = null;
    state.observedHpa = null;
    for (const el of document.querySelectorAll('#huPresets .hu-chip')) el.classList.remove('active');
}

function loadPreset(id) {
    const e = BY_ID[id];
    if (!e) return;
    const inp = toCycloneInput(e);
    Object.assign(state, {
        lat: inp.lat, lon: inp.lon, presetId: id,
        observedHpa: e.pressureHpa ?? null, site: null,
    });
    setWind(e.vmaxKmh);
    setForward(e.forwardMs * KMH_PER_MS);
    setHeading(e.headingDeg);
    $('huCity').value = '';
    for (const el of document.querySelectorAll('#huPresets .hu-chip')) {
        el.classList.toggle('active', el.dataset.id === id);
    }
    apply({ refit: true });
    report('preset', { id });
}

function buildPresetChips() {
    const wrap = $('huPresets');
    const events = CFG.events || {};
    wrap.innerHTML = CYCLONES.map((c) => {
        const name = (events[c.key] || {}).name || c.id;
        return `<button type="button" class="hu-chip" data-id="${esc(c.id)}">${esc(name)} · ${c.vmaxKmh} ${esc(T.kmh)}</button>`;
    }).join('');
    wrap.addEventListener('click', (ev) => {
        const b = ev.target.closest('.hu-chip');
        if (b) loadPreset(b.dataset.id);
    });
}

function wireCitySearch() {
    const input = $('huCity'), box = $('huSugg');
    let items = [], active = -1, seq = 0;
    const close = () => { box.classList.remove('open'); box.innerHTML = ''; items = []; active = -1; };
    const choose = (c) => {
        state.site = { lat: c.lat, lon: c.lng ?? c.lon, name: c.name, pop: c.pop };
        input.value = c.name;
        close();
        apply({ refit: true });
        report('site_pick', { how: 'search' });
    };
    input.addEventListener('input', () => {
        const q = input.value.trim();
        if (q.length < 2 || !window.LocationInput) { close(); return; }
        /* ⚠️ searchWithFallback ВСЕГДА возвращает промис (на промахе он ещё
           подтягивает файл малых городов РФ), поэтому его результат нельзя
           читать как массив. Читали — и подсказки не появлялись НИ РАЗУ с
           запуска тула: `promise.length` это undefined, ветка «ничего не
           нашлось» закрывала список, ошибки при этом не было. Жалоба
           01.09.2026: набрано «аделаида» (город в наборе есть), выпадающего
           списка нет. */
        const mine = ++seq;
        Promise.resolve(window.LocationInput.loadData()).then(
            () => (window.LocationInput.searchWithFallback || window.LocationInput.search)(q, 7)
        ).then((hits) => {
            // Человек уже дописал дальше — этот ответ протух. Без проверки
            // медленный запрос за «аде» перетирает список для «аделаида».
            if (mine !== seq) return;
            /* search() отдаёт ССЫЛКИ на строки набора — {i, score, pop}, а не
               города. Имя, координаты и страну даёт rowToCity(i). Без него
               кнопка подсказки рисуется с пустым текстом: список открыт, в нём
               пусто, и выбрать нечего. */
            items = (hits || []).map((h) => window.LocationInput.rowToCity(h.i));
            if (!items.length) { close(); return; }
            box.innerHTML = items.map((c, i) =>
                `<button type="button" role="option" data-i="${i}">${esc(c.name)}<small>${esc(c.cc || '')}</small></button>`).join('');
            box.classList.add('open');
            active = -1;
        }).catch(close);
    });
    box.addEventListener('click', (e) => {
        const b = e.target.closest('button[data-i]');
        if (b) choose(items[+b.dataset.i]);
    });
    input.addEventListener('keydown', (e) => {
        if (!items.length) return;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            active = (active + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length;
            for (const b of box.querySelectorAll('button')) b.setAttribute('aria-selected', String(+b.dataset.i === active));
        } else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); choose(items[active]); }
        else if (e.key === 'Escape') close();
    });
    document.addEventListener('click', (e) => { if (!box.contains(e.target) && e.target !== input) close(); });
}

/** Through the shared primitive — see the note in the earthquake tool for why
 *  a hand-rolled call site fails silently on hardened Firefox builds. */
function wireLocate() {
    $('huLocate').addEventListener('click', () => {
        const geo = window.__geo;
        const panel = $('huGeoPanel');
        if (!geo) return;
        const pre = geo.available();
        if (!pre.ok) { geo.panel(panel, geo.explain(pre.reason)); return; }
        geo.get({ enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }).then((p) => {
            const { latitude: lat, longitude: lon } = p.coords;
            let name = null;
            if (citiesReady) {
                const near = citiesWithin(lat, lon, 60, 1);
                if (near.length) name = near[0].name;
            }
            state.site = { lat, lon, name };
            $('huCity').value = name || `${lat.toFixed(3)}, ${lon.toFixed(3)}`;
            if (panel) panel.innerHTML = '';
            apply({ refit: true });
            report('site_pick', { how: 'geo' });
        }).catch((err) => geo.panel(panel, geo.explain(err)));
    });
}

function report(stage, data) {
    try { window.__aeFunnel && window.__aeFunnel(stage, data || {}); } catch (e) {}
}

/* ────────────────────────────────  boot  ───────────────────────────── */

/* Leaflet can come late or not at all. When its own copy does not answer, the
 * layout's fallback ladder brings the mirror's copy AFTER this module ran (the
 * mirror's address has an «@», so the copy is appended, not written in place).
 * Measured on the dev stand with the own copy refused: the map did come up, but
 * only by luck of timing, the streamlines never did, and the plugin — then a tag
 * right after Leaflet's — threw «Cannot read properties of undefined (reading
 * 'DomUtil')» (Sentry TIMBRICA-FRONTEND-NZ, the same class on /wind-map). With no
 * Leaflet at all the stage stayed an empty black box. So: wait for Leaflet, run
 * the plugin only then, and say in words when Leaflet never came. */
function leafletReady() {
    if (window.L && window.L.map) return Promise.resolve();
    if (window.__whenGlobal) return window.__whenGlobal('L', 15000);
    return Promise.reject(new Error('L'));
}

/* The streamlines are optional: the map is correct without them, they join when
   the plugin has run. */
function velocityReady() {
    if (window.L.velocityLayer) return Promise.resolve();
    const urls = ((CFG.lib && CFG.lib.velocity) || []).slice();
    const nonced = document.querySelector('script[nonce]');
    return new Promise((resolve, reject) => {
        (function next() {
            if (!urls.length) { reject(new Error('velocity')); return; }
            const s = document.createElement('script');
            s.src = urls.shift();
            if (nonced && nonced.nonce) s.nonce = nonced.nonce;
            s.onload = () => { if (window.L.velocityLayer) resolve(); else next(); };
            s.onerror = next;
            document.head.appendChild(s);
        })();
    });
}

function libFailed() {
    const box = $('huLibFail');
    if (box) box.hidden = false;
    try { window.__ae && window.__ae('tool_error', { slug: 'hurricane-simulator', where: 'lib', error_type: 'lib_fail' }); } catch (e) {}
    // The ladder's last copy may still arrive: then the map comes up after all.
    const late = () => {
        if (mapView || !(window.L && window.L.map)) return;
        document.removeEventListener('timbrica:script-recovered', late);
        start();
    };
    document.addEventListener('timbrica:script-recovered', late);
    window.addEventListener('load', late);
}

function boot() {
    leafletReady().then(start, libFailed);
}

function start() {
    if (mapView) return;
    const box = $('huLibFail');
    if (box) box.hidden = true;

    paintLegend($('huLegendBar'));
    buildPresetChips();
    mapView = createMap($('huMap'), CFG);
    mapView.onPick((lat, lon) => {
        const hint = $('huPickHint');
        if (hint) hint.classList.add('gone');
        clearPreset();
        state.lat = lat; state.lon = lon;
        apply();
        report('eye_move', {});
    });

    $('huWind').addEventListener('input', (e) => { clearPreset(); setWind(+e.target.value); apply(); });
    $('huForward').addEventListener('input', (e) => { clearPreset(); setForward(+e.target.value); apply(); });
    $('huHeading').addEventListener('input', (e) => { clearPreset(); setHeading(+e.target.value); apply(); });
    $('huWind').addEventListener('change', () => report('wind_set', { kmh: state.vmaxKmh }));

    wireCitySearch();
    wireLocate();

    loadPreset(CFG.home);
    mapView.invalidate();
    setTimeout(() => { const h = $('huPickHint'); h && h.classList.add('gone'); }, 10000);

    if (window.LocationInput) {
        Promise.resolve(window.LocationInput.loadData())
            .then(() => { citiesReady = buildIndex(); if (citiesReady) apply(); })
            .catch(() => {});
    }

    velocityReady().then(() => apply(), () => {});

    try { window.__stage && window.__stage('ready'); } catch (e) {}
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
